import type { AddressViewModel } from '@generated';
import { useQuery } from '@tanstack/react-query';
import { useAppSettings } from '~/AppSettingsContext';

export interface AzureMapsSearchResult {
	id: string;
	type: string;
	address: {
		freeformAddress: string;
		streetNumber?: string;
		streetName?: string;
		municipality?: string;
		countrySecondarySubdivision?: string;
		countrySubdivision?: string;
		countrySubdivisionName?: string;
		countrySubdivisionCode?: string;
		postalCode?: string;
		extendedPostalCode?: string;
		countryCode?: string;
		country?: string;
		countryCodeISO3?: string;
		localName?: string;
	};
	position: {
		lat: number;
		lon: number;
	};
}

interface AzureMapsResponse {
	summary: {
		query: string;
		queryType: string;
		queryTime: number;
		numResults: number;
		offset: number;
		totalResults: number;
		fuzzyLevel: number;
	};
	results: AzureMapsSearchResult[];
}

export function convertToAddressViewModel(result: AzureMapsSearchResult): AddressViewModel {
	if (!result.address.localName || !result.address.countrySubdivision || !result.address.postalCode) {
		throw new Error('Incomplete address data from Azure Maps');
	}
	return {
		addressOne:
			result.address.streetNumber && result.address.streetName ? `${result.address.streetNumber} ${result.address.streetName}`.trim() : 'N/A',
		city: result.address.localName,
		state: result.address.countrySubdivision,
		zipCode: result.address.postalCode,
		zipCodeExtension: result.address.extendedPostalCode ? result.address.extendedPostalCode.split('-')[1] : undefined,
		county: result.address.countrySecondarySubdivision,
		latitude: result.position.lat,
		longitude: result.position.lon,
		mapIntegrationId: result.id,
		formattedAddress: result.address.freeformAddress,
		notes: undefined,
		aptSuiteNumber: undefined
	};
}

export function useAzureMapsAutocomplete(searchText: string) {
	const { atlasSubscriptionKey } = useAppSettings();
	const fetchPlacePredictions = async (searchText: string): Promise<AzureMapsSearchResult[]> => {
		const url = new URL('https://atlas.microsoft.com/search/address/json');
		url.searchParams.set('api-version', '1.0');
		url.searchParams.set('subscription-key', atlasSubscriptionKey);
		url.searchParams.set('query', searchText);
		url.searchParams.set('typeahead', 'true');
		url.searchParams.set('limit', '10');

		const response = await fetch(url.toString());
		if (!response.ok) throw new Error('Search failed');

		const data: AzureMapsResponse = await response.json();
		return data.results || [];
	};

	return useQuery({
		queryKey: ['azure-maps-autocomplete', searchText],
		queryFn: () => fetchPlacePredictions(searchText),
		enabled: Boolean(searchText) && searchText.trim().length > 3,
		select: results => results.map(convertToAddressViewModel)
	});
}
